import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import TetfundLogo from '../../../assets/images/TetfundLogo.svg';
import { Eye, EyeOff, Search, ChevronDown } from 'lucide-react';
import { lookupService, LookupDto } from '../../../services/lookupService';
import { authorAuthService, SignupPayload, ProfileData } from '../services/authorAuth';
import PasswordStrengthChecker, { isPasswordStrong } from '../../sharedComponents/PasswordStrengthChecker';

// Tells the user which required fields still need attention before they can continue.
const MissingFields: React.FC<{ fields: string[] }> = ({ fields }) => {
  if (fields.length === 0) return null;
  return (
    <div role="alert" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm">
      <p className="font-semibold mb-1">Please complete the following to continue:</p>
      <ul className="list-disc pl-5 space-y-0.5">
        {fields.map(field => <li key={field}>{field}</li>)}
      </ul>
    </div>
  );
};

const Register: React.FC = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  // Set once the user presses Next / Create account with incomplete fields.
  const [showMissing, setShowMissing] = useState(false);
  // Set when the entered email belongs to an existing verified + profile-complete
  // account — we then skip the form and just register the newly picked role.
  const [existingUser, setExistingUser] = useState<ProfileData | null>(null);

  // Lookups State
  const [titles, setTitles] = useState<LookupDto[]>([]);
  const [states, setStates] = useState<LookupDto[]>([]);
  const [qualifications, setQualifications] = useState<LookupDto[]>([]);
  const [ranks, setRanks] = useState<LookupDto[]>([]);
  const [institutionCategories] = useState<LookupDto[]>([
    { id: 1, name: 'University', active: true },
    { id: 2, name: 'Polytechnic', active: true },
    { id: 3, name: 'College', active: true },
  ]);
  const [institutions, setInstitutions] = useState<LookupDto[]>([]);

  const [formData, setFormData] = useState({
    titleId: '', genderId: '', firstName: '', lastName: '',
    otherName: '', email: '', phone: '',
    address: '', stateId: '', institutionId: '', qualificationId: '',
    rankId: '', password: '', confirmPassword: '', agreeTerms: false,
    userType: '',
    affiliationType: '', // Frontend-only: "academia" or "non_academia"
    applicantLocation: '', // "nigeria" or "diaspora"; frontend-only
    institutionCategoryId: '',
    institutionIsPublic: '', // "true" or "false"
    affiliateInstitution: '', // free text field for diaspora applicants
  });

  const [institutionSearch, setInstitutionSearch] = useState('');
  const [isSearchingInstitutions, setIsSearchingInstitutions] = useState(false);
  
  const isJournal = formData.userType === 'journal_rep';

  // Fetch baseline lookups
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [titlesData, statesData, qualsData, ranksData] = await Promise.all([
          lookupService.getTitles(), lookupService.getStates(),
          lookupService.getQualifications(), lookupService.getRanks()
        ]);
        setTitles(titlesData || []); 
        setStates(statesData || []);
        setQualifications(qualsData || []); 
        setRanks(ranksData || []);
      } catch (error) { /* handled by global interceptor */ }
    };
    fetchLookups();
  }, []);

  // Handle click outside to close the custom dropdown panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced execution for fetching and filtering institutions
  const debouncedInstitutionSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string, categoryId: string, isPublic: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (categoryId && isPublic) {
            try {
              setIsSearchingInstitutions(true);
              const instData = await lookupService.getInstitutions(
                parseInt(categoryId),
                isPublic === 'true'
              );
              
              if (searchTerm.trim()) {
                const filtered = (instData || []).filter(inst =>
                  inst.name.toLowerCase().includes(searchTerm.toLowerCase())
                );
                setInstitutions(filtered);
              } else {
                setInstitutions(instData || []);
              }
            } catch (error) {
              setInstitutions([]);
            } finally {
              setIsSearchingInstitutions(false);
            }
          } else {
            setInstitutions([]);
          }
        }, 400); // Clean 400ms pause response window
      };
    })(),
    []
  );

  // Trigger searching when category, public designation, or query pauses
  useEffect(() => {
    if (formData.affiliationType === 'academia' && formData.applicantLocation === 'nigeria') {
      debouncedInstitutionSearch(
        institutionSearch,
        formData.institutionCategoryId,
        formData.institutionIsPublic
      );
    }
  }, [formData.affiliationType, formData.institutionCategoryId, formData.institutionIsPublic, formData.applicantLocation, institutionSearch, debouncedInstitutionSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Changing the email or role invalidates any prior existing-account detection.
    if (name === 'email' || name === 'userType') setExistingUser(null);

    setFormData(prev => {
      let updated = { ...prev, [name]: value };
  
      if (name === 'institutionCategoryId' || name === 'institutionIsPublic') {
        updated.institutionId = '';
      }
  
      if (name === 'applicantLocation' || name === 'affiliationType') {
        updated = {
          ...updated,
          applicantLocation: name === 'affiliationType' ? '' : updated.applicantLocation,
          rankId: name === 'affiliationType' && value === 'non_academia' ? '' : updated.rankId,
          institutionCategoryId: '',
          institutionIsPublic: '',
          institutionId: '',
          affiliateInstitution: ''
        };
      }
  
      return updated;
    });
  
    if (name === 'institutionCategoryId' || name === 'institutionIsPublic') {
      setInstitutionSearch('');
    }
  };

  const handleSelectInstitution = (id: number) => {
    setFormData(prev => ({ ...prev, institutionId: id.toString() }));
    setIsOpenDropdown(false);
  };

  const handleEmailBlur = async () => {
    const email = formData.email;
    const roleId = formData.userType === 'author' ? 1 : formData.userType === 'journal_rep' ? 13 : null;

    if (!email || !isValidEmail(email)) return;

    if (!roleId) {
      Swal.fire({
        icon: 'info',
        title: 'Select User Type',
        text: 'Please select a user type before entering your email.',
        confirmButtonColor: '#00A63E'
      });
      setFormData(prev => ({ ...prev, email: '' }));
      return;
    }

    setExistingUser(null);
    setIsCheckingEmail(true);
    try {
      // 1. Already registered for THIS role → blocked.
      const roleRes = await authorAuthService.checkUsernameExistForRole(email, roleId);
      if (!roleRes.isSuccess) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: roleRes.message || 'Could not validate email at this time.',
          confirmButtonColor: '#00A63E'
        });
        setFormData(prev => ({ ...prev, email: '' }));
        return;
      }
      if (roleRes.data !== false) {
        Swal.fire({
          icon: 'warning',
          title: 'Email Already Registered for this Role',
          text: 'This email already has the selected role. Please choose a different role.',
          confirmButtonColor: '#00A63E'
        });
        setFormData(prev => ({ ...prev, email: '' }));
        return;
      }

      // 2. Does the email exist under another role at all?
      const existRes = await authorAuthService.checkUsernameExist(email);
      if (!existRes.isSuccess || existRes.data !== true) return; // brand-new → normal form

      // 3. Existing person: only skip the form if verified AND profile complete.
      const userRes = await authorAuthService.getUserByUsername(email);
      if (userRes.isSuccess && userRes.data?.isVerified && userRes.data.person?.profileCompleted) {
        setExistingUser(userRes.data);
      }
      // Not verified / incomplete → fall through to the normal registration form.
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'An error occurred while validating your email.',
        confirmButtonColor: '#00A63E'
      });
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Light path: existing verified account just adds the newly picked role, using
  // the person's stored details as the payload (no re-entry, empty password).
  const handleLightPathRegister = async () => {
    if (!existingUser) return;
    const roleId = formData.userType === 'author' ? 1 : 13;
    const p = existingUser.person;

    setLoading(true);
    try {
      const payload: any = {
        firstName: p.firstName,
        lastName: p.lastName,
        otherName: p.otherName || '',
        email: p.email,
        phoneNo: p.phoneNo,
        password: '',
        titleId: p.titleId,
        institutionId: p.institutionId ?? null,
        dob: null,
        address: p.address,
        qualificationId: p.qualificationId || 0,
        sexId: p.sexId || 0,
        rankId: p.rankId || 0,
        stateId: p.stateId || 0,
        roleId,
        affiliateInstitution: p.affiliateInstitution || '',
        isOtherInstitution: p.isOtherInstitution === true,
        alreadyHasAnAccount: true,
      };

      const response = await authorAuthService.signup(payload);
      if (response.isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful!',
          text: 'A verification link has been sent to your email. Please verify your account to activate the new role, then log in.',
          confirmButtonColor: '#00A63E'
        }).then(() => navigate('/login'));
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: response.message || 'Could not complete registration. Please try again.',
          confirmButtonColor: '#00A63E'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while registering. Please try again.',
        confirmButtonColor: '#00A63E'
      });
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = formData.userType === 'journal_rep' ? 'Professional Journal Representative' : 'Manuscript Author';

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Labels of the step 1 fields still missing or invalid; empty means the step is complete.
  const getStep1Missing = () => {
    const {
      titleId, genderId, firstName, lastName, email,
      phone, address, stateId, userType
    } = formData;

    const missing: string[] = [];
    if (!userType) missing.push('User type');
    if (!email) missing.push('Email address');
    else if (!isValidEmail(email)) missing.push('A valid email address');
    if (!titleId) missing.push('Title');
    if (!genderId) missing.push('Gender');
    if (!firstName.trim()) missing.push('First name');
    if (!lastName.trim()) missing.push('Last name');
    if (!phone.trim()) missing.push('Phone number');
    if (!address.trim()) missing.push('Official address');
    if (!stateId) missing.push('State of origin');
    return missing;
  };

  // Labels of the step 2 fields still missing or invalid; empty means the step is complete.
  const getStep2Missing = () => {
    const {
      qualificationId, rankId, password, confirmPassword,
      agreeTerms, userType, institutionId, applicantLocation,
      affiliateInstitution, institutionCategoryId, institutionIsPublic, affiliationType
    } = formData;

    const missing: string[] = [];
    if (!userType) missing.push('User type (go back to step 1)');

    const isJournal = userType === 'journal_rep';

    if (!isJournal) {
      if (!affiliationType) {
        missing.push('Affiliation type');
      } else if (!applicantLocation) {
        missing.push(affiliationType === 'academia' ? 'Institution / country of residence location' : 'Location');
      } else if (affiliationType === 'academia') {
        // Academia must always name an institution, in Nigeria or the diaspora.
        if (applicantLocation === 'nigeria') {
          if (!institutionCategoryId) missing.push('Institution category');
          if (!institutionIsPublic) missing.push('Institution type');
          if (!institutionId) missing.push('Institution');
        } else if (affiliateInstitution.trim().length <= 2) {
          missing.push('Affiliate institution name');
        }
      }
      // Non-academia: the organisation / institution name is optional.

      // Journal reps skip qualification & rank
      if (!qualificationId) missing.push('Highest qualification');
      if (!rankId) missing.push('Academic rank');
    }

    if (!password) missing.push('Password');
    else if (!isPasswordStrong(password)) missing.push('A password that meets all the strength requirements');
    if (!confirmPassword) missing.push('Confirm password');
    else if (password !== confirmPassword) missing.push('Matching passwords');
    if (!agreeTerms) missing.push('Accept the Terms & Conditions and Privacy Policy');
    return missing;
  };

  const step1Missing = getStep1Missing();
  const step2Missing = getStep2Missing();
  const isStep1Valid = () => step1Missing.length === 0 && !isCheckingEmail;
  const isStep2Valid = () => step2Missing.length === 0;

  const handleNextStep = () => {
    if (!isStep1Valid()) {
      setShowMissing(true);
      return;
    }
    setShowMissing(false);
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isStep2Valid()) {
      setShowMissing(true);
      return;
    }

    setLoading(true);

    const usesAffiliateInstitution =
      formData.affiliationType === 'non_academia' || formData.applicantLocation === 'diaspora';

    if (usesAffiliateInstitution && formData.affiliationType === 'academia') {
      if (!formData.affiliateInstitution || !formData.affiliateInstitution.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Missing Institution',
          text: 'Please enter the organisation or institution you are affiliated with.',
          confirmButtonColor: '#00A63E'
        });
        setLoading(false);
        return;
      }
    }
    try {
      const usesAffiliateInstitution =
        formData.affiliationType === 'non_academia' || formData.applicantLocation === 'diaspora';
      
      const payload: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        otherName: formData.otherName || '',
        email: formData.email,
        phoneNo: formData.phone,
        password: formData.password,
        titleId: parseInt(formData.titleId),
        institutionId: !usesAffiliateInstitution && formData.institutionId ? parseInt(formData.institutionId) : null,
        dob: null, // Standard timestamp inclusion
        address: formData.address,
        qualificationId: parseInt(formData.qualificationId) || 0,
        sexId: parseInt(formData.genderId) || 0,
        rankId: parseInt(formData.rankId) || 0,
        stateId: parseInt(formData.stateId) || 0,
        roleId: formData.userType === 'author' ? 1 : 13,
        affiliateInstitution: usesAffiliateInstitution ? formData.affiliateInstitution.trim() : '',
        isOtherInstitution: usesAffiliateInstitution,
        alreadyHasAnAccount: false
      };
      const response = await authorAuthService.signup(payload);

      if (response.isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful!',
          text: 'Check your email for account verification link',
          confirmButtonColor: '#00A63E'
        }).then(() => {
          navigate('/verify-account', { state: { email: formData.email } });
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: response.message || 'Please check your details and try again.',
          confirmButtonColor: '#00A63E'
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.message || 'An unexpected error occurred.',
        confirmButtonColor: '#00A63E'
      });
    } finally {
      setLoading(false);
    }
  };

  const showTermsModal = () => {
    Swal.fire({
      title: 'Terms & Conditions',
      html: `
        <div class="text-left text-sm space-y-4 max-h-96 overflow-y-auto pr-2">
          <p>Welcome to TETFund TAG Portal. By using our services, you agree to these terms.</p>
          <h4 class="font-bold">1. Use of Service</h4>
          <p>You must provide accurate information during registration. You are responsible for maintaining the security of your account.</p>
          <h4 class="font-bold">2. Data Submission</h4>
          <p>All academic manuscripts submitted must be original and comply with TETFund guidelines.</p>
          <h4 class="font-bold">3. Privacy</h4>
          <p>Your data is handled according to our Privacy Policy.</p>
          <p>Last updated: ${new Date().toLocaleDateString()}</p>
        </div>
      `,
      confirmButtonText: 'Close',
      confirmButtonColor: '#00A63E'
    });
  };

  const showPrivacyModal = () => {
    Swal.fire({
      title: 'Privacy Policy',
      html: `
        <div class="text-left text-sm space-y-4 max-h-96 overflow-y-auto pr-2">
          <p>Your privacy is important to us. This policy explains how we collect and use your data.</p>
          <h4 class="font-bold">1. Information Collection</h4>
          <p>We collect personal and professional information necessary for manuscript processing and TETFund approval.</p>
          <h4 class="font-bold">2. Use of Information</h4>
          <p>Data is used exclusively for evaluating submissions and communicating with authors.</p>
          <h4 class="font-bold">3. Security</h4>
          <p>We implement robust security measures to protect your data from unauthorized access.</p>
          <p>Last updated: ${new Date().toLocaleDateString()}</p>
        </div>
      `,
      confirmButtonText: 'Close',
      confirmButtonColor: '#00A63E'
    });
  };

  const inputClass = "w-full bg-[#F3F4F6] border-none rounded-md py-3 px-4 text-gray-700 focus:ring-2 focus:ring-[#00A63E] outline-none transition-all placeholder:text-gray-400";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

  // Quick display resolve for selected item
  const selectedInstitutionName = institutions.find(i => i.id.toString() === formData.institutionId)?.name;

  return (
    <div className="flex min-h-screen min-h-dvh bg-white">
      {/* LEFT SIDE - BRANDING */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#088A61] flex-col justify-between p-16 text-white sticky top-0 h-screen">
        <div>
          <div className="flex items-center gap-3">
            <img src={TetfundLogo} alt="Logo" className="bg-white p-1.5 rounded h-10" />
          </div>
        </div>
        <div className="max-w-xl">
          <h1 className="text-5xl font-normal leading-tight mb-8">
            Manuscript Submission for Academic Book Publication and Professional Journal Support
          </h1>
          <p className="text-lg opacity-90 leading-relaxed font-light text-center">
            <span className="opacity-90 leading-relaxed font-bold text-center">Tetfund Tag System (TTS) v 1.0</span>
          </p>
        </div>
        <p className="text-sm opacity-70">© {new Date().getFullYear()} TETFund TAG. All rights reserved.</p>
      </div>

      {/* RIGHT SIDE - FORM */}
      <div className="w-full lg:w-1/2 flex justify-center py-6 md:py-12 px-6 md:px-16">
        <div className="w-full max-w-xl">
          <header className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-1">Create an account</h2>
            {existingUser ? (
              <p className="text-gray-500 text-sm">This email already has an account</p>
            ) : (
              <>
                <p className="text-gray-500 text-sm">
                  {step === 1 ? 'Step 1 of 2: Account & Personal Info' : 'Step 2 of 2: Professional Info & Security'}
                </p>
                <div className="flex gap-2 mt-4">
                  <div className={`h-1.5 flex-1 rounded-full ${step >= 1 ? 'bg-[#00A63E]' : 'bg-gray-200'}`}></div>
                  <div className={`h-1.5 flex-1 rounded-full ${step === 2 ? 'bg-[#00A63E]' : 'bg-gray-200'}`}></div>
                </div>
              </>
            )}
          </header>

          {existingUser ? (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div className="bg-[#F0FDF4] border border-green-100 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">You already have an account</h3>
                <p className="text-sm text-gray-600">
                  This email is already registered and verified. You don’t need to fill in your details again. Register it
                  as <span className="font-semibold">{roleLabel}</span> to add this role to your account.
                </p>
              </div>
              <button
                type="button"
                onClick={handleLightPathRegister}
                disabled={loading}
                className={`w-full py-3.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2
                  ${loading ? 'bg-[#A7F3D0] text-white cursor-not-allowed' : 'bg-[#00A63E] text-white hover:bg-[#008c34]'}`}
              >
                {loading ? <span className="animate-pulse">Registering...</span> : <>Register as {roleLabel} <span className="text-lg">✓</span></>}
              </button>
              <button
                type="button"
                onClick={() => { setExistingUser(null); setFormData(prev => ({ ...prev, email: '' })); }}
                disabled={loading}
                className="w-full py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-60"
              >
                Use a different email
              </button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {step === 1 ? (
              <div className="space-y-6 animate-in fade-in duration-500">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Account</h3>
                <p className="text-sm text-gray-500 -mt-3">
                  Tell us what you're registering as and enter your email to get started.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>User type</label>
                    <select name="userType" value={formData.userType} onChange={handleChange} className={inputClass}>
                      <option value="">Select user type</option>
                      <option value="author">Manuscript Author</option>
                      <option value="journal_rep">Professional Journal Representative</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Email address</label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        placeholder="name@university.edu"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleEmailBlur}
                        className={`${inputClass} ${isCheckingEmail ? 'pr-10' : ''}`}
                      />
                      {isCheckingEmail && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-[#00A63E]/20 border-t-[#00A63E] rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    {formData.email && !isValidEmail(formData.email) && <span className="text-xs text-red-500 mt-1 block">Please enter a valid email</span>}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 pt-4">Personal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Title</label>
                    <select name="titleId" value={formData.titleId} onChange={handleChange} className={inputClass}>
                      <option value="">Select title</option>
                      {titles.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Gender</label>
                    <select name="genderId" value={formData.genderId} onChange={handleChange} className={inputClass}>
                      <option value="">Select gender</option>
                      <option value="1">Male</option>
                      <option value="2">Female</option>
                    </select>
                  </div>
                  <div className="col-span-1">
                    <label className={labelClass}>First name</label>
                    <input type="text" name="firstName" placeholder="John" value={formData.firstName} onChange={handleChange} className={inputClass} />
                  </div>
                  <div className="col-span-1">
                    <label className={labelClass}>Last name</label>
                    <input type="text" name="lastName" placeholder="Doe" value={formData.lastName} onChange={handleChange} className={inputClass} />
                  </div>
                  <div className="col-span-1">
                    <label className={labelClass}>Other name</label>
                    <input type="text" name="otherName" placeholder="Middle name (Optional)" value={formData.otherName} onChange={handleChange} className={inputClass} />
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 pt-4">Contact Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1">
                    <label className={labelClass}>Phone number</label>
                    <input type="tel" name="phone" placeholder="+234..." value={formData.phone} onChange={handleChange} className={inputClass} />
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <label className={labelClass}>Official Address</label>
                    <input type="text" name="address" placeholder="Enter official address" value={formData.address} onChange={handleChange} className={inputClass} />
                  </div>
                  <div className="col-span-1">
                    <label className={labelClass}>State of Origin</label>
                    <select name="stateId" value={formData.stateId} onChange={handleChange} className={inputClass}>
                      <option value="">Select state</option>
                      {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>

                {showMissing && <MissingFields fields={step1Missing} />}

                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={isCheckingEmail}
                  className={`w-full mt-6 py-3.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2
                    ${isStep1Valid() ? 'bg-[#374151] text-white hover:bg-black' : 'bg-gray-200 text-gray-500'}`}
                >
                  {isCheckingEmail ? 'Checking email...' : <>Next Step <span className="text-xl">→</span></>}
                </button>
              </div>
            ) : (
              <div className="space-y-6 animate-in fade-in duration-500">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Professional Information</h3>
                <div className="space-y-4">
                  
                  {/* APPLICANT LOCATION RADIO SELECTOR */}
                  {!isJournal && (
                  <div>
                    <label className={labelClass}>Affiliation Type *</label>
                    <select name="affiliationType" value={formData.affiliationType} onChange={handleChange} className={inputClass}>
                      <option value="">Select affiliation type</option>
                      <option value="academia">Academia</option>
                      <option value="non_academia">Non Academia</option>
                    </select>
                  </div>
                  )}

                  {!isJournal && formData.affiliationType && (
                  <div>
                    <label className={labelClass}>
                      {formData.affiliationType === 'academia'
                        ? 'Institution / Country of Residence Location *'
                        : 'Location *'}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1.5">
                      <label className="flex items-center gap-3 bg-[#F3F4F6] p-3 rounded-md cursor-pointer border border-transparent has-[:checked]:border-[#00A63E] transition-all">
                        <input
                          type="radio"
                          name="applicantLocation"
                          value="nigeria"
                          checked={formData.applicantLocation === 'nigeria'}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#00A63E] focus:ring-[#00A63E] border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700">Nigeria</span>
                      </label>
                      
                      <label className="flex items-center gap-3 bg-[#F3F4F6] p-3 rounded-md cursor-pointer border border-transparent has-[:checked]:border-[#00A63E] transition-all">
                        <input
                          type="radio"
                          name="applicantLocation"
                          value="diaspora"
                          checked={formData.applicantLocation === 'diaspora'}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#00A63E] focus:ring-[#00A63E] border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700">Diaspora</span>
                      </label>
                    </div>
                  </div>
                  )}

                  {!isJournal && formData.affiliationType === 'academia' && formData.applicantLocation === 'nigeria' ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in duration-300">
                        <div>
                          <label className={labelClass}>Institution Category</label>
                          <select name="institutionCategoryId" value={formData.institutionCategoryId} onChange={handleChange} className={inputClass}>
                            <option value="">Select category</option>
                            {institutionCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>Institution Type</label>
                          <select name="institutionIsPublic" value={formData.institutionIsPublic} onChange={handleChange} className={inputClass}>
                            <option value="">Select type</option>
                            <option value="true">Public</option>
                            <option value="false">Private</option>
                          </select>
                        </div>
                      </div>

                      {/* UNIFIED CUSTOM SEARCHABLE DROPDOWN */}
                      <div className="relative animate-in fade-in duration-300" ref={dropdownRef}>
                        <label className={labelClass}>
                          Institution {formData.userType === 'author' && <span className="text-red-500">*</span>}
                        </label>

                        {/* Fake Select Input Button */}
                        <button
                          type="button"
                          disabled={!formData.institutionCategoryId || !formData.institutionIsPublic}
                          onClick={() => setIsOpenDropdown(!isOpenDropdown)}
                          className={`${inputClass} text-left flex items-center justify-between ${
                            (!formData.institutionCategoryId || !formData.institutionIsPublic) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                          }`}
                        >
                          <span className={selectedInstitutionName ? 'text-gray-900' : 'text-gray-400'}>
                            {!formData.institutionCategoryId || !formData.institutionIsPublic
                              ? 'Select category and type first'
                              : selectedInstitutionName || 'Select institution'}
                          </span>
                          <ChevronDown size={18} className="text-gray-500" />
                        </button>

                        {/* Integrated search and selection list box container */}
                        {isOpenDropdown && formData.institutionCategoryId && formData.institutionIsPublic && (
                          <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden flex flex-col max-h-72">
                            
                            {/* Search field stuck to the very top inside the dropdown layer */}
                            <div className="p-2 border-b border-gray-100 bg-gray-50 relative flex items-center">
                              <Search className="absolute left-5 text-gray-400" size={16} />
                              <input
                                type="text"
                                placeholder="Type to filter..."
                                value={institutionSearch}
                                onChange={(e) => setInstitutionSearch(e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded py-2 pl-9 pr-8 text-sm outline-none focus:ring-1 focus:ring-[#00A63E] focus:border-[#00A63E]"
                                autoFocus
                              />
                              {isSearchingInstitutions && (
                                <div className="absolute right-5">
                                  <div className="w-3.5 h-3.5 border-2 border-[#00A63E]/20 border-t-[#00A63E] rounded-full animate-spin"></div>
                                </div>
                              )}
                            </div>

                            {/* Dropdown Menu Options */}
                            <div className="overflow-y-auto flex-1 max-h-48 divide-y divide-gray-50">
                              {institutions.length === 0 ? (
                                <div className="p-4 text-sm text-gray-500 text-center">
                                  {isSearchingInstitutions ? 'Loading list...' : 'No institutions found'}
                                </div>
                              ) : (
                                institutions.map(inst => (
                                  <button
                                    key={inst.id}
                                    type="button"
                                    onClick={() => handleSelectInstitution(inst.id)}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#00A63E]/10 transition-colors text-gray-700 ${
                                      formData.institutionId === inst.id.toString() ? 'bg-[#00A63E]/5 font-medium text-[#00A63E]' : ''
                                    }`}
                                  >
                                    {inst.name}
                                  </button>
                                ))
                              )}
                            </div>

                            {/* Item counts counter strip */}
                            {institutions.length > 0 && (
                              <div className="bg-gray-50 px-4 py-1.5 border-t border-gray-100 text-[11px] text-gray-400 font-medium">
                                Showing {institutions.length} results
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : !isJournal && formData.applicantLocation && (formData.affiliationType === 'non_academia' || formData.applicantLocation === 'diaspora') ?  (
                    /* DIASPORA / NON-ACADEMIA FREE TEXT INSTITUTION INPUT */
                    <div className="animate-in fade-in duration-300">
                      <label htmlFor="affiliateInstitution" className={labelClass}>
                        {formData.affiliationType === 'non_academia'
                          ? <>Organisation or Institution You Are Affiliated With <span className="text-gray-400 font-normal">(Optional)</span></>
                          : <>Affiliate Institution Name <span className="text-red-500">*</span></>}
                      </label>
                      <input
                        type="text"
                        id="affiliateInstitution"
                        name="affiliateInstitution"
                        placeholder={formData.affiliationType === 'non_academia'
                          ? 'Enter the organisation or institution you are affiliated with'
                          : 'Enter the name of your foreign affiliate institution'}
                        value={formData.affiliateInstitution}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                  ): null}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Highest Qualification</label>
                      <select name="qualificationId" value={formData.qualificationId} onChange={handleChange} className={inputClass}>
                        <option value="">Select qualification</option>
                        {qualifications.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Academic Rank</label>
                      <select name="rankId" value={formData.rankId} onChange={handleChange} className={inputClass}>
                        <option value="">Select rank</option>
                        {ranks.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 pt-4">Security & Terms</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className={labelClass}>Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Create password"
                        value={formData.password}
                        onChange={handleChange}
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <PasswordStrengthChecker password={formData.password} />
                  </div>
                  <div className="relative">
                    <label className={labelClass}>Confirm password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        placeholder="Re-enter password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <span className="text-xs text-red-500 mt-1 block">Passwords do not match</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={(e) => setFormData(p => ({ ...p, agreeTerms: e.target.checked }))}
                    className="w-4 h-4 text-[#00A63E] border-gray-300 rounded focus:ring-[#00A63E]"
                  />
                  <label htmlFor="agreeTerms" className="text-sm text-gray-600">
                    I accept the <button type="button" onClick={showTermsModal} className="text-[#155DFC] font-medium hover:underline">Terms & Conditions</button> and <button type="button" onClick={showPrivacyModal} className="text-[#155DFC] font-medium hover:underline">Privacy Policy</button>
                  </label>
                </div>

                {showMissing && <MissingFields fields={step2Missing} />}

                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={() => { setShowMissing(false); setStep(1); }} className="flex-1 py-3.5 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all">
                    ← Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex-1 py-3.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2
                      ${isStep2Valid() ? 'bg-[#00A63E] text-white hover:bg-[#008c34]' : 'bg-[#A7F3D0] text-white'}`}
                  >
                    {loading ? (
                      <span className="animate-pulse">Creating...</span>
                    ) : (
                      <>Create account <span className="text-lg">✓</span></>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
          )}

          <p className="text-center mt-8 text-gray-600">
            Already have an account? <Link to="/login" className="text-[#155DFC] font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
